module Client.IllyAdmin.Actions.Hilos

open Types
open Elmish
open Shared
open Shared.IllyAdmin.Hilos
open Fable.React
open Fable.React.Props
open Illy.SafeStack.Client
open Illy.SafeStack.Client.jQueryUI
open Client
open SafeStack.Client

type Row = 
    {
        Hilo: HiloMetadata
        Visible: bool
    }

type Model =
    {
        Search: Text.Model
        DebouncedSearchString: SearchString Debounce.Model
        Hilos: Row list
        Loading: bool
    }

type Msg =
    | LoadedData of HiloMetadata List
    | ApiError of ApiError
    | SearchMsg of Text.Msg
    | DebounceMsg of SearchString Debounce.Msg
    | FilterHilos of string option

module Calls =
    let getHilos () =
        Api.secureCall<IHilosIllyAdminApi,_,_,_> SecuredRoute.IllyAdmin (fun api -> api.GetMetadata) () LoadedData ApiError

module Init =
    let search value =
        Text.CustomSettings.defaultOptions()
        |> Text.init "hilo-search" "Search String" (Some "Type here to search") 200.0 false value

    let debounce () = Debounce.init (System.TimeSpan.FromMilliseconds 300.0) SearchString.empty

let init () =
    {
        Search = Init.search None
        DebouncedSearchString = Init.debounce ()
        Hilos = []
        Loading = true
    }, Calls.getHilos ()

module Dispatch =
    let debounceMsg msg model =
        let m, cmd =
            Debounce.updateWithCmd 
                msg
                DebounceMsg
                model.DebouncedSearchString
                (fun x -> { model with DebouncedSearchString = x })
                (SearchString.toString >> FilterHilos >> Cmd.ofMsg)

        m, cmd |> SideEffect.Cmd

    let filterHilos (msg: string option) model =
        let isVisible (ss: string) hilo =
            hilo.Column.ToLower().Contains ss || hilo.TargetTables.ToLower().Contains ss || hilo.HiloTable.ToLower().Contains ss

        let newHilos =
            match msg with
            | Some searchString ->
                let ss = searchString.ToLower()
                model.Hilos |> List.map (fun r -> { r with Visible = isVisible ss r.Hilo })
            | None ->
                model.Hilos |> List.map (fun r -> { r with Visible = true })

        { model with
            Hilos = newHilos
        }, SideEffect.none

let update msg model =
    match msg with
    | LoadedData data ->
        { model with
            Hilos = data |> List.sortStringsByCI (fun r -> r.TargetTables) |> List.map (fun m -> { Hilo = m; Visible = true })
            Loading = false
        }, SideEffect.none
    | ApiError err -> { model with Loading = false }, err |> AppError.ApiError |> ActionEvent.AppError |> SideEffect.Event
    | SearchMsg msg ->
        let m, cmd = Text.update msg model.Search
        { model with Search = m }, Cmd.batch [ cmd; Debounce.inputCmd (m |> Text.toString |> SearchString.ofString) DebounceMsg ] |> SideEffect.Cmd
    | DebounceMsg msg ->
        Dispatch.debounceMsg msg model
    | FilterHilos msg ->
        Dispatch.filterHilos msg model

let view model dispatch = 
    [
        div [ Id "hilo-metadata" ] [
            Text.textInput model.Loading model.Search (SearchMsg >> dispatch)
            jQueryUI.simpleTable 
                "No Hi-Los in the system - this shouldn't be the case" 
                [
                    Some [ str "Target Table(s)" ] ===> fun h -> [ str h.TargetTables ]
                    Some [ str "Hi/Lo Table" ] ===> fun h -> [ str h.HiloTable ]
                    Some [ str "Column" ] ===> fun h -> [ str h.Column ]
                    Some [ str "Size" ] ===> fun h -> [ str $"%i{h.Value}" ]
                ] 
                model.Loading
                (model.Hilos |> List.choose (fun r -> if r.Visible then Some r.Hilo else None))
        ]
    ]

let metaData =
    {
        Key = BrowseHilos
        Title = "Hi/Los"
        Description = "List all Hi/Los in the system, with their Hi/Lo values and names"
        Group = ActionGroup.Config
    }